<template>
  <crud-modal
    :show="show"
    title="Nuevo Servicio"
    size="lg"
    :loading="loading"
    :show-save="true"
    save-text="Guardar"
    cancel-text="Cancelar"
    @save="onSubmit"
    @close="$emit('close')"
  >
    <form @submit.prevent="onSubmit">
      <!-- Selector de Integrador cuando el filtro global está en 'Todos' -->
      <div v-if="isTodosIntegrador" class="form-group mb-3">
        <label for="modal_integrador_select" class="font-weight-bold small text-muted mb-1">
          Integrador <span class="text-danger">*</span>
        </label>
        <select
          class="form-control"
          name="id_integrador"
          id="modal_integrador_select"
          v-model="selectedIntegradorId"
          @change="onIntegradorSelected"
          required
        >
          <option :value="null">Seleccione un integrador...</option>
          <option
            v-for="item in integradoresList"
            :key="item.id_integrador || item.id"
            :value="item.id_integrador || item.id"
          >
            {{ item.integrador }}
          </option>
        </select>
        <small class="form-text text-muted">
          Seleccione el integrador para cargar los operadores, productos y keywords asociados.
        </small>
      </div>
      <div v-else class="form-group mb-3">
        <label class="font-weight-bold small text-muted mb-1">
          Integrador
        </label>
        <input
          type="text"
          class="form-control"
          :value="getIntegradorName(idIntegrador)"
          disabled
          readonly
        />
        <small class="form-text text-muted">
          Integrador seleccionado para el nuevo servicio (no editable).
        </small>
      </div>

      <!-- Spinner indicador de carga de catálogos -->
      <div v-if="loadingCatalogos" class="alert alert-light border text-center my-3 py-2">
        <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
        <span class="text-muted small font-weight-bold">Cargando operadores, productos y keywords del integrador...</span>
      </div>

      <div class="row">
        <!-- Operador -->
        <div class="col-md-4">
          <div class="form-group">
            <label for="operador_select" class="font-weight-bold small text-muted mb-1">
              Operador <span class="text-danger">*</span>
            </label>
            <select
              class="form-control"
              id="operador_select"
              name="id_operador"
              v-model="form.id_operador"
              :disabled="loadingCatalogos || (isTodosIntegrador && !selectedIntegradorId)"
            >
              <option :value="null">
                {{ isTodosIntegrador && !selectedIntegradorId ? "Seleccione un integrador primero" : "Seleccionar" }}
              </option>
              <option
                v-for="operadorSel in resolvedOperadores"
                :key="operadorSel.id_operador"
                :value="operadorSel.id_operador"
              >
                {{ operadorSel.numero_corto || operadorSel.numero || operadorSel.operador }}
              </option>
            </select>
            <small class="form-text text-muted">Operador y numeración corta asociada.</small>
          </div>
        </div>

        <!-- Producto -->
        <div class="col-md-4">
          <div class="form-group">
            <label for="producto_select" class="font-weight-bold small text-muted mb-1">
              Producto <span class="text-danger">*</span>
            </label>
            <select
              class="form-control"
              id="producto_select"
              name="id_producto"
              v-model="form.id_producto"
              :disabled="loadingCatalogos || (isTodosIntegrador && !selectedIntegradorId)"
            >
              <option :value="null">
                {{ isTodosIntegrador && !selectedIntegradorId ? "Seleccione un integrador primero" : "Seleccionar" }}
              </option>
              <option
                v-for="productoItem in resolvedProductos"
                :key="productoItem.id_producto"
                :value="productoItem.id_producto"
              >
                {{ productoItem.producto }}
              </option>
            </select>
            <small class="form-text text-muted">Producto asignado al servicio.</small>
          </div>
        </div>

        <!-- Keyword -->
        <div class="col-md-4">
          <div class="form-group">
            <label for="keyword_select" class="font-weight-bold small text-muted mb-1">
              Keywords <span class="text-danger">*</span>
            </label>
            <select
              class="form-control"
              id="keyword_select"
              name="id_keyword"
              v-model="form.id_keyword"
              :disabled="loadingCatalogos || (isTodosIntegrador && !selectedIntegradorId)"
            >
              <option :value="null">
                {{ isTodosIntegrador && !selectedIntegradorId ? "Seleccione un integrador primero" : "Seleccionar" }}
              </option>
              <option
                v-for="keywordItem in resolvedKeywords"
                :key="keywordItem.id_keyword"
                :value="keywordItem.id_keyword"
              >
                {{ keywordItem.keyword }}
              </option>
            </select>
            <small class="form-text text-muted">Palabra clave disparadora del servicio.</small>
          </div>
        </div>
      </div>

      <!-- Errores -->
      <div v-if="errors" class="alert alert-danger my-2" role="alert">
        <i class="bi bi-exclamation-triangle me-1"></i>
        {{ formattedError }}
      </div>
    </form>
  </crud-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import CrudModal from "../utiles/CrudModal.vue";

export default {
  name: "ServicioFormModal",
  components: {
    CrudModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    operation: {
      type: String,
      default: "add",
    },
    servicio: {
      type: Object,
      default: () => ({}),
    },
    tipoSuscripciones: {
      type: [Array, Object],
      default: () => [],
    },
    operadores: {
      type: [Array, Object],
      default: () => [],
    },
    productos: {
      type: [Array, Object],
      default: () => [],
    },
    keywords: {
      type: [Array, Object],
      default: () => [],
    },
    idIntegrador: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadingCatalogos: false,
      errors: null,
      selectedIntegradorId: null,
      modalOperadores: [],
      modalProductos: [],
      modalKeywords: [],
      form: {
        id_servicio: null,
        id_tipo_suscripcion: null,
        id_operador: null,
        id_producto: null,
        id_keyword: null,
        keyword: null,
        activo: 1,
      },
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),

    isTodosIntegrador() {
      return !this.idIntegrador || this.idIntegrador === "todos";
    },

    integradoresList() {
      let list = [];
      if (this.integradores && this.integradores.data && this.integradores.data.length > 0) {
        list = [...this.integradores.data];
      } else if (this.user && this.user.integradores && Array.isArray(this.user.integradores)) {
        list = [...this.user.integradores];
      }
      return list.sort((a, b) =>
        String(a.integrador || "").localeCompare(String(b.integrador || ""), undefined, { sensitivity: "base" })
      );
    },

    resolvedOperadores() {
      if (this.isTodosIntegrador) {
        return this.selectedIntegradorId ? this.modalOperadores : [];
      }
      if (this.modalOperadores && this.modalOperadores.length) {
        return this.modalOperadores;
      }
      if (!this.operadores) return [];
      if (Array.isArray(this.operadores)) return this.operadores;
      if (this.operadores.data && Array.isArray(this.operadores.data)) {
        return this.operadores.data;
      }
      return [];
    },

    resolvedProductos() {
      if (this.isTodosIntegrador) {
        return this.selectedIntegradorId ? this.modalProductos : [];
      }
      if (this.modalProductos && this.modalProductos.length) {
        return this.modalProductos;
      }
      if (!this.productos) return [];
      if (Array.isArray(this.productos)) return this.productos;
      if (this.productos.data && Array.isArray(this.productos.data)) {
        return this.productos.data;
      }
      return [];
    },

    resolvedKeywords() {
      if (this.isTodosIntegrador) {
        return this.selectedIntegradorId ? this.modalKeywords : [];
      }
      if (this.modalKeywords && this.modalKeywords.length) {
        return this.modalKeywords;
      }
      if (!this.keywords) return [];
      if (Array.isArray(this.keywords)) return this.keywords;
      if (this.keywords.data && Array.isArray(this.keywords.data)) {
        return this.keywords.data;
      }
      return [];
    },

    formattedError() {
      if (!this.errors) return "";
      if (typeof this.errors === "string") return this.cleanDuplicateError(this.errors);
      if (typeof this.errors === "object") {
        if (this.errors.errors && typeof this.errors.errors === "object" && Object.keys(this.errors.errors).length > 0) {
          const details = Object.entries(this.errors.errors)
            .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(", ") : errs}`)
            .join(" | ");
          const mainErr = this.errors.error || this.errors.message;
          return mainErr ? `${this.cleanDuplicateError(mainErr)} (${details})` : details;
        }
        if (this.errors.error) return this.cleanDuplicateError(this.errors.error);
        if (this.errors.message) return this.cleanDuplicateError(this.errors.message);
        return JSON.stringify(this.errors);
      }
      return String(this.errors);
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.errors = null;
        this.form = {
          id_servicio: null,
          id_tipo_suscripcion: null,
          id_operador: null,
          id_producto: null,
          id_keyword: null,
          keyword: null,
          activo: 1,
        };
        if (this.isTodosIntegrador) {
          this.selectedIntegradorId = null;
          this.modalOperadores = [];
          this.modalProductos = [];
          this.modalKeywords = [];
        } else {
          this.selectedIntegradorId = this.idIntegrador;
          this.fetchCatalogosForIntegrador(this.selectedIntegradorId);
        }
      }
    },
    idIntegrador(newVal) {
      if (!this.isTodosIntegrador && newVal) {
        this.selectedIntegradorId = newVal;
        if (this.show) {
          this.fetchCatalogosForIntegrador(newVal);
        }
      }
    },
  },
  methods: {
    cleanDuplicateError(err) {
      if (!err) return "";
      const errStr = String(err);
      if (errStr.includes("1062") || errStr.includes("Duplicate entry")) {
        const keyMatch = errStr.match(/for key ['"]([^'"]+)['"]/);
        if (keyMatch) {
          const rawKey = keyMatch[1].split(".").pop();
          if (rawKey.toUpperCase() === "PRIMARY") {
            return "Ya existe un registro con ese identificador.";
          }
          if (rawKey.startsWith("UNI_")) {
            const clean = rawKey.substring(4);
            const [table, colsStr] = clean.includes("__")
              ? clean.split("__")
              : [clean.split("_")[0], clean.split("_").slice(1).join("_")];
            const tableLabel = (table || "registro").replace(/_/g, " ").trim();
            const cols = (colsStr || "")
              .split("_")
              .filter((c) => c !== "id")
              .join(" y ");
            return `Ya existe un ${tableLabel} con ese ${cols}.`;
          }
        }
        return "Ya existe un registro con esos datos duplicados.";
      }
      return errStr;
    },

    getIntegradorName(id) {
      if (!id) return "";
      const list = this.integradoresList;
      const found = list.find((i) => String(i.id_integrador || i.id) === String(id));
      return found ? found.integrador : id;
    },

    onIntegradorSelected() {
      this.errors = null;
      this.form.id_operador = null;
      this.form.id_producto = null;
      this.form.id_keyword = null;
      if (this.selectedIntegradorId) {
        this.fetchCatalogosForIntegrador(this.selectedIntegradorId);
      } else {
        this.modalOperadores = [];
        this.modalProductos = [];
        this.modalKeywords = [];
      }
    },

    fetchCatalogosForIntegrador(integradorId) {
      if (!integradorId || integradorId === "todos") {
        this.modalOperadores = [];
        this.modalProductos = [];
        this.modalKeywords = [];
        return Promise.resolve();
      }

      this.loadingCatalogos = true;
      const pOperadores = axios.post(process.env.VUE_APP_API_URL + "operador/busqueda", {
        id_integrador: integradorId,
        get_all: true,
        activo: 1,
        order_by: { field: "numero_corto", order: "ASC" },
      });

      const pProductos = axios.post(process.env.VUE_APP_API_URL + "producto/busqueda", {
        id_integrador: integradorId,
        get_all: true,
        activo: 1,
        order_by: { field: "id_producto", order: "ASC" },
      });

      const pKeywords = axios.post(process.env.VUE_APP_API_URL + "keyword/list", {
        id_integrador: integradorId,
        service: true,
      });

      return Promise.allSettled([pOperadores, pProductos, pKeywords])
        .then(([resOp, resProd, resKw]) => {
          if (resOp.status === "fulfilled" && resOp.value.data) {
            const list = resOp.value.data.data || resOp.value.data;
            this.modalOperadores = Array.isArray(list)
              ? [...list].sort(
                  (a, b) =>
                    Number(a.numero_corto || a.numero || 0) -
                    Number(b.numero_corto || b.numero || 0)
                )
              : [];
          } else {
            this.modalOperadores = [];
          }

          if (resProd.status === "fulfilled" && resProd.value.data) {
            const list = resProd.value.data.data || resProd.value.data;
            this.modalProductos = Array.isArray(list) ? list : [];
          } else {
            this.modalProductos = [];
          }

          if (resKw.status === "fulfilled" && resKw.value.data) {
            const list = resKw.value.data.data || resKw.value.data;
            this.modalKeywords = Array.isArray(list) ? list : [];
          } else {
            this.modalKeywords = [];
          }
        })
        .finally(() => {
          this.loadingCatalogos = false;
        });
    },

    onSubmit() {
      if (this.isTodosIntegrador && !this.selectedIntegradorId) {
        this.errors = "Debe seleccionar un integrador antes de continuar";
        return;
      }

      if (
        !this.form.id_operador ||
        !this.form.id_producto ||
        !this.form.id_keyword
      ) {
        this.errors = "Seleccione los campos requeridos antes de continuar";
        return;
      }

      this.errors = null;
      this.loading = true;
      const targetIntegrador = this.isTodosIntegrador ? this.selectedIntegradorId : this.idIntegrador;
      const payload = Object.assign({}, this.form, {
        id_integrador: targetIntegrador,
      });
      delete payload.atributos;

      axios
        .post(process.env.VUE_APP_API_URL + "servicio/new", payload)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data;
          } else {
            const createdSrv = (response.data && response.data.data) ? Object.assign({}, response.data.data) : {
              id_servicio: response.data && (response.data.id_servicio || response.data.id),
              id_producto: this.form.id_producto,
              id_operador: this.form.id_operador,
              id_keyword: this.form.id_keyword,
              id_tipo_suscripcion: this.form.id_tipo_suscripcion,
              id_integrador: targetIntegrador,
              keyword: this.form.keyword,
              activo: this.form.activo !== undefined ? this.form.activo : 1,
            };
            if (!createdSrv.id_servicio && response.data && response.data.id_servicio) {
              createdSrv.id_servicio = response.data.id_servicio;
            }
            if (!createdSrv.id_integrador) {
              createdSrv.id_integrador = targetIntegrador;
            }

            Swal.fire({
              title: "Bien!",
              text: "Servicio creado correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
            this.$emit("created", createdSrv);
            this.$emit("saved");
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && (e.response.data.error || e.response.data.errors)) {
            this.errors = e.response.data;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar crear el servicio",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
